import { useCallback, useMemo } from 'react';
import { useFormik } from 'formik';

import { getQuillPlainText } from '../../../../components/baseV2';
import { FormRichTextField, FormTextField } from '../../components';
import { TEXT_AREA_MAX_LENGTH } from '../../constants';
import {
  CULTURAL_CONSIDERATION_RICH_TEXT_SCHEMA,
  CULTURAL_CONSIDERATION_SCHEMA,
} from '../validationSchema';

import type { CulturalConsiderationFormValues } from '../types';
import type { CulturalConsiderationFieldProps } from './types';

export default function CulturalConsiderationField({
  culturalConsideration,
  disabled,
  onSave,
  richText = false,
}: CulturalConsiderationFieldProps) {
  const validationSchema = richText
    ? CULTURAL_CONSIDERATION_RICH_TEXT_SCHEMA
    : CULTURAL_CONSIDERATION_SCHEMA;
  const initialValues = useMemo<CulturalConsiderationFormValues>(
    () => ({ cultural_consideration: culturalConsideration }),
    [culturalConsideration],
  );
  const formik = useFormik<CulturalConsiderationFormValues>({
    initialValues,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema,
    onSubmit: () => {},
  });
  const handleBlur = useCallback(
    (_name: string, value?: string) => {
      const nextValue = value ?? formik.values.cultural_consideration;
      if (!validationSchema.isValidSync({ cultural_consideration: nextValue })) {
        return;
      }
      onSave(richText ? (getQuillPlainText(nextValue) ? nextValue : '') : nextValue.trim());
    },
    [formik.values.cultural_consideration, onSave, richText, validationSchema],
  );

  if (richText) {
    return (
      <FormRichTextField
        disabled={disabled}
        formik={formik}
        label="Cultural considerations"
        maxLength={TEXT_AREA_MAX_LENGTH}
        name="cultural_consideration"
        onBlur={handleBlur}
        placeholder="Enter any cultural considerations"
      />
    );
  }

  return (
    <FormTextField
      disabled={disabled}
      formik={formik}
      label="Cultural considerations"
      maxLength={TEXT_AREA_MAX_LENGTH}
      minRows={4}
      multiline
      name="cultural_consideration"
      onBlur={handleBlur}
      placeholder="Enter any cultural considerations"
    />
  );
}
